import {
  CARD_CODES,
  CardCodes,
  CorporateCardConnection,
  getOrganizationLogo,
} from '@revenuemarket/bank';
import { Flex } from '@revenuemarket/cds';
import { theme } from '@revenuemarket/design';
import { overlays } from '@revenuemarket/overlays';
import { Convert } from '@revenuemarket/react';
import { Refresh } from '@revenuemarket/ui';
import { Alert, Button, Card, List, Typography } from 'antd';
import dayjs from 'dayjs';
import Image from 'next/image';
import { CardScrapeStatus } from '~apis/scrape';
import { getAnalyzeStatusTag } from '~components/ScrapeStatus/ListForHometax';
import { CardOrganizationConnectionDrawer } from '~components/ui/Drawer/CardDrawer/CardOrganizationConnectionDrawer';

interface ListForCorpCardProps {
  datasource: CardScrapeStatus[];
  title?: string;
  scrapeProps?: ScrapeProps;
  connections: CorporateCardConnection[];
}

const getTypeText = (item: CardScrapeStatus) => {
  if (item.type === 'APPROVAL') {
    return '승인내역';
  }
  if (item.type === 'PURCHASE') {
    return '매입내역';
  }
  if (item.type === 'BILLING') {
    return '청구내역';
  }
  if (item.type === 'LIMIT') {
    return '한도';
  }
  if (item.type === 'PRE_PAYMENT') {
    return '즉시결제내역';
  }
  return '';
};

interface AbnormalCardOrganization {
  organizationCode: CardCodes;
  totalCount: number;
  abnormalCount: number;
}

interface ScrapeProps {
  onScrape: () => void;
  title: string;
  disabled?: boolean;
}

const ListForCorpCard = ({ datasource, title, scrapeProps, connections }: ListForCorpCardProps) => {
  const titleNode = title ? (
    <Flex direction="row" align="center" justify="space-between">
      <Typography.Title level={5}>{title}</Typography.Title>
      {scrapeProps && (
        <Button
          onClick={() => scrapeProps.onScrape()}
          icon={<Refresh width={16} height={16} fill={theme.colorTokens.iconDefault} />}
          disabled={scrapeProps.disabled}
          style={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {scrapeProps.title}
        </Button>
      )}
    </Flex>
  ) : null;

  const abnormalCardOrganizations = Object.values(
    connections
      .filter((connection) => {
        return !connection.purgedAt;
      })
      .reduce<Record<string, AbnormalCardOrganization>>((acc, connection) => {
        const { organizationCode } = connection.organization;
        if (!acc[organizationCode]) {
          acc[organizationCode] = {
            organizationCode: organizationCode as CardCodes,
            totalCount: 0,
            abnormalCount: 0,
          };
        }
        acc[organizationCode].totalCount += 1;
        if (connection.status === 'DISCONNECTED') {
          acc[organizationCode].abnormalCount += 1;
        }
        return acc;
      }, {}),
  ).filter((org) => org.abnormalCount > 0);

  return (
    <Card title={titleNode} bordered={false}>
      <Flex direction="column" gap={abnormalCardOrganizations?.length == 0 ? 0 : 12}>
        <Flex direction="column" gap={abnormalCardOrganizations?.length == 0 ? 0 : 12}>
          {abnormalCardOrganizations.map(({ organizationCode, totalCount, abnormalCount }) => {
            const organizationName = CARD_CODES[organizationCode as CardCodes];
            return (
              <Alert
                key={organizationCode}
                message={`${organizationName}에 로그인할 수 없습니다`}
                description={`등록된 ${organizationName} 로그인 계정 ${totalCount}개 중 ${abnormalCount}개가 로그인 불가합니다. 자세히 버튼을 클릭해 연동 불가 계정 삭제 후 새로운 계정을 추가해주세요.`}
                type="error"
                showIcon
                banner
                action={
                  <Flex gap={4} align="center">
                    <Button
                      size="small"
                      onClick={() => {
                        overlays.open(({ isOpen, onClose }) => (
                          <CardOrganizationConnectionDrawer
                            isOpen={isOpen}
                            onClose={onClose}
                            organizationCode={organizationCode as CardCodes}
                          />
                        ));
                      }}
                    >
                      자세히
                    </Button>
                  </Flex>
                }
              />
            );
          })}
        </Flex>
        <List
          itemLayout="horizontal"
          dataSource={datasource}
          rowKey={(item) => `${item.organizationCode}_${item.type}`}
          renderItem={(item) => (
            <List.Item>
              <Flex align="center" gap={12} style={{ width: '100%' }}>
                <Flex justify="space-between" align="center" style={{ width: '100%' }}>
                  <Flex gap={12} align="center">
                    <Image
                      width={16}
                      height={24}
                      src={getOrganizationLogo(item.organizationCode).x1}
                      alt={CARD_CODES[item.organizationCode]}
                    />
                    <Typography.Text>
                      {`${CARD_CODES[item.organizationCode]} ${getTypeText(item)}`}
                    </Typography.Text>
                  </Flex>
                  <Flex gap={12} align="center">
                    <Convert
                      value={item.scrapedAt}
                      expression={(scrapedAt) => (
                        <Typography.Text>
                          {dayjs(scrapedAt).format('YYYY-MM-DD HH:mm:ss')}
                        </Typography.Text>
                      )}
                    />
                    {getAnalyzeStatusTag(item.status)}
                  </Flex>
                </Flex>
              </Flex>
            </List.Item>
          )}
        />
      </Flex>
    </Card>
  );
};

export default ListForCorpCard;
